import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import moment from 'moment';
import { Modal } from 'react-bootstrap';
import { Prod01 } from '../../constants/ActionType';
import { formatDateTime } from '../../utils/dateFormat';
import AssignDeviceDialog from '../Devices/AssignDeviceDialog';
import { imeiLast5 } from '../../utils/patientForm';

export const UNASSIGN_REASONS = ['Returned', 'Broken', 'Lost', 'Replaced', 'Patient deactivated', 'Other'];
const fmt = (d) => (d ? moment(d).format('MM/DD/YYYY') : '—');
const errText = (e) => e?.response?.data?.message || e.message;

/**
 * RPM devices on the patient edit page: list (type, IMEI masked to last 5, assigned date, last reading),
 * assign by scan/typed 15-digit IMEI and unassign with reason + date. Server decides who may manage (canManage).
 */
const PatientDevicesSection = ({ patient, onChanged }) => {
    const [data, setData] = useState({ devices: [], canManage: false });
    const [assignOpen, setAssignOpen] = useState(false);
    const [msg, setMsg] = useState(null);
    const [pending, setPending] = useState(null);
    const [busy, setBusy] = useState(false);
    const name = `${patient?.firstname || ''} ${patient?.lastname || ''}`.trim();

    const load = useCallback(async () => {
        if (!patient?._id) return;
        try { setData((await axios.get(`${Prod01}/patient/form/${patient._id}/devices`)).data); } catch (e) { setMsg({ v: 'danger', t: errText(e) }); }
    }, [patient?._id]);
    useEffect(() => { load(); }, [load]);

    const confirm = async () => {
        setBusy(true);
        try {
            if (pending.action === 'assign') {
                await axios.post(`${Prod01}/inventory/assign`, { deviceId: pending.device._id, patientId: patient._id });
                setMsg({ v: 'success', t: `Device …${imeiLast5(pending.device.imei)} assigned to ${name}.` });
            } else {
                await axios.post(`${Prod01}/inventory/unassign`, {
                    deviceId: pending.device._id, patientId: patient._id, reason: pending.reason, effectiveDate: pending.date,
                    ...(pending.reason === 'Other' ? { reasonText: pending.text } : {}),
                });
                setMsg({ v: 'success', t: `Device …${pending.device.imeiLast5} unassigned from ${name} (${pending.reason}).` });
            }
            setPending(null);
            load();
            if (onChanged) onChanged();
        } catch (e) { setMsg({ v: 'danger', t: errText(e) }); }
        setBusy(false);
    };
    const last5 = pending ? pending.device.imeiLast5 || imeiLast5(pending.device.imei) : '';
    const unassignInvalid = pending?.action === 'unassign' && (!pending.reason || !pending.date || (pending.reason === 'Other' && (pending.text || '').trim().length < 2));

    return (
        <div className="mt-3" data-testid="patient-devices">
            <h6 style={{ color: '#004aad' }}><strong>Devices</strong></h6>
            <table className="table table-sm" style={{ fontSize: 13 }}>
                <thead><tr><th>Type</th><th>IMEI (last 5)</th><th>Assigned</th><th>Last reading</th>{data.canManage ? <th /> : null}</tr></thead>
                <tbody>
                    {data.devices.length ? data.devices.map((d) => (
                        <tr key={d._id}>
                            <td>{d.deviceType || '—'}</td>
                            <td style={{ fontFamily: 'monospace' }}>{d.imeiMasked}</td>
                            <td>{formatDateTime(d.assignedAt, { fallback: '—' })}</td>
                            <td>{fmt(d.lastReadingAt)}</td>
                            {data.canManage ? (
                                <td><button type="button" className="btn btn-outline-danger btn-sm py-0" onClick={() => setPending({ action: 'unassign', device: d, reason: '', date: moment().format('YYYY-MM-DD'), text: '' })}>Unassign</button></td>
                            ) : null}
                        </tr>
                    )) : <tr><td colSpan={5} className="text-muted">No devices assigned.</td></tr>}
                </tbody>
            </table>
            {data.canManage ? (
                <button type="button" className="btn btn-primary btn-sm" data-testid="assign-device-btn" onClick={() => { setMsg(null); setAssignOpen(true); }}>
                    <i className="fas fa-barcode"></i> Assign device
                </button>
            ) : <small className="text-muted">View only — device assignment is limited to admin, admin staff, HR lead and enrollment.</small>}
            {msg ? <div className={`alert alert-${msg.v} py-1 mt-2 mb-0`} role="status" style={{ fontSize: 13 }}>{msg.t}</div> : null}

            <AssignDeviceDialog show={assignOpen} patient={patient} onHide={() => setAssignOpen(false)}
                onAssigned={(text) => { setAssignOpen(false); setMsg({ v: 'success', t: text }); load(); if (onChanged) onChanged(); }} />

            <Modal show={!!pending} onHide={() => !busy && setPending(null)} centered>
                <Modal.Header closeButton><Modal.Title style={{ fontSize: 18 }}>{pending?.action === 'assign' ? 'Assign device' : 'Unassign device?'}</Modal.Title></Modal.Header>
                <Modal.Body>
                    <p className="mb-2">Patient: <strong>{name}</strong><br />Device IMEI (last 5): <strong>{last5}</strong>{pending?.device?.deviceType ? ` · ${pending.device.deviceType}` : ''}</p>
                    {pending?.action === 'unassign' ? (
                        <>
                            <label htmlFor="unassign-reason" className="mb-0">Reason</label>
                            <select id="unassign-reason" className="form-control form-control-sm mb-2" value={pending.reason} onChange={(e) => setPending({ ...pending, reason: e.target.value })}>
                                <option value="">Select reason</option>
                                {UNASSIGN_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
                            </select>
                            {pending.reason === 'Other' ? (
                                <><label htmlFor="unassign-text" className="mb-0">Details</label>
                                <input id="unassign-text" className="form-control form-control-sm mb-2" value={pending.text} onChange={(e) => setPending({ ...pending, text: e.target.value })} /></>
                            ) : null}
                            <label htmlFor="unassign-date" className="mb-0">Date</label>
                            <input id="unassign-date" type="date" className="form-control form-control-sm" value={pending.date} onChange={(e) => setPending({ ...pending, date: e.target.value })} />
                        </>
                    ) : null}
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={() => setPending(null)}>Cancel</button>
                    <button type="button" className={`btn btn-sm ${pending?.action === 'assign' ? 'btn-primary' : 'btn-danger'}`} disabled={busy || unassignInvalid} onClick={confirm}>
                        {pending?.action === 'assign' ? 'Confirm assign' : 'Confirm unassign'}
                    </button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default PatientDevicesSection;
